import React, { useState, useEffect, useRef } from "react";
import styles from "./OurTechnology.module.scss";
import SkewedBarSVG from "@/components/Common/SkewedBarSVG";
import Image from "next/image";
import { useRouter } from "next/router";
import { getCDNImageUrl } from "@/utils/Common";

const technologyData = [
  {
    id: 1,
    heading: "Real-Time Shipment Tracking",
    description:
      "Stay informed with order status updates and milestone alerts from dispatch to delivery, keeping you and your team in control",
    imageSrc: getCDNImageUrl("Real-Time-Tracking.webp"),
  },
  {
    id: 2,
    heading: "Centralized Documentation",
    description:
      "Access all regulatory and quality documents in one place — from CoAs and SDS to certifications and technical specs",
    imageSrc: getCDNImageUrl("Centralized-Documentation.webp"),
  },
  {
    id: 3,
    heading: "Faster Turnaround Times",
    description:
      "Our tech-enabled operations reduce manual steps and bottlenecks, enabling quicker response times and more dependable fulfillment",
    imageSrc: getCDNImageUrl("Faster-Turnaround-Times.webp"),
  },
];

function OurTechnology({
  hideLearnMore = false,
  title = "Backed by Technology That Delivers Better Results",
  description = "Our internal platform streamlines operations, improves communication, and gives your team faster, more reliable service from first request to final delivery.",
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [fadeClass, setFadeClass] = useState("");
  const sliderRef = useRef(null);
  const router = useRouter();

  // Minimum swipe distance required to trigger slide change
  const minSwipeDistance = 50;

  // Function to change slide with fade effect
  const changeSlideWithFade = (newIndex) => {
    if (newIndex === currentSlide) return;

    setFadeClass("fadeOut");
    setTimeout(() => {
      setCurrentSlide(newIndex);
      setFadeClass("fadeIn");
      setTimeout(() => {
        setFadeClass("");
      }, 250);
    }, 250);
  };

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      const nextSlide =
        currentSlide === technologyData.length - 1 ? 0 : currentSlide + 1;
      changeSlideWithFade(nextSlide);
    }, 2500); // Change slide every 2.5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentSlide]);

  const handleDotClick = (index) => {
    changeSlideWithFade(index);
    setIsAutoPlaying(false); // Pause auto-scroll when user manually selects
    // Resume auto-scroll after 8 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const handleCarouselHover = () => {
    setIsAutoPlaying(false);
  };

  const handleCarouselLeave = () => {
    setIsAutoPlaying(true);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentSlide < technologyData.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }

    // Resume auto-scroll after swipe
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const onClickLearnMore = () => {
    router.push("/elchemy-tech");
  };

  return (
    <div className={styles.ourTechnology}>
      {/* Radial Gradient */}
      <div className={styles.centerGradient}></div>
      {/* Small Heading */}
      {!hideLearnMore && <h6>OUR TECHNOLOGY</h6>}
      {/* Long Heading Section */}
      <div className={styles.longHeadingSection}>
        <div>
          <h2>{title}</h2>
          <p className={styles.longHeadingSectionDescription}>{description}</p>
        </div>
        {/* Learn More Button */}
        {!hideLearnMore && (
          <button onClick={onClickLearnMore} className={styles.learnMoreButton}>
            Learn More
          </button>
        )}
      </div>

      {/* Desktop Carousel */}
      <div
        className={styles.desktopCarousel}
        onMouseEnter={handleCarouselHover}
        onMouseLeave={handleCarouselLeave}
      >
        {/* Left Section - Heading Description and Pagination Dots  */}
        <div className={styles.desktopCarouselLeftSection}>
          {/* Heading and description */}
          <div className={styles.desktopCarouselHeadingDescriptionContainer}>
            <div className={styles.desktopCarouselHeadingContainer}>
              {/*  Bar   */}
              <SkewedBarSVG />
              <h4 className={styles[fadeClass]}>
                {technologyData[currentSlide].heading}
              </h4>
            </div>
            <p className={styles[fadeClass]}>
              {technologyData[currentSlide].description}
            </p>
          </div>
          {/* Pagination Dots */}
          <div className={styles.paginationContainer}>
            {technologyData.map((_, index) => (
              <button
                key={index}
                className={`${styles.paginationDot} ${
                  index === currentSlide ? styles.paginationDotActive : ""
                }`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        </div>
        {/* Right Section - Image */}
        <div className={styles.desktopCarouselImageContainer}>
          <Image
            src={technologyData[currentSlide].imageSrc}
            alt={`technology-image-${currentSlide + 1}`}
            fill
            className={styles.desktopCarouselImage}
          />
        </div>
      </div>

      {/* Mobile Carousel */}
      <div className={styles.mobileCarouselWrapper}>
        <div className={styles.mobileCarouselContainer}>
          <div
            ref={sliderRef}
            className={styles.mobileCarouselSlider}
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {technologyData.map((tech, index) => (
              <div key={tech.id} className={styles.mobileCarouselCard}>
                {/* Content Section - Heading and Description */}
                <div className={styles.mobileCarouselContentSection}>
                  <div className={styles.mobileCarouselHeadingContainer}>
                    {/* Bar */}
                    <SkewedBarSVG />
                    <h4>{tech.heading}</h4>
                  </div>
                  <p className={styles.mobileCarouselDescription}>
                    {tech.description}
                  </p>
                </div>

                {/* Image Section */}
                <div className={styles.mobileCarouselImageContainer}>
                  <Image
                    src={tech.imageSrc}
                    alt={`technology-image-mobile-${index + 1}`}
                    fill
                    className={styles.mobileCarouselImage}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Pagination Dots - Outside Container */}
        <div className={styles.mobilePaginationContainer}>
          {technologyData.map((_, index) => (
            <button
              key={index}
              className={`${styles.mobilePaginationDot} ${
                index === currentSlide ? styles.mobilePaginationDotActive : ""
              }`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default OurTechnology;
